// CommandPalette — registers ALL Cmd-K shortcuts manually so we get exactly
// one entry per resource (no duplicate "List" + category-header entries from
// RefineKbar's auto-registration), AND renders the palette's own visible UI.
//
// Sections:
//   Navigation        – top-level pages
//   Browse            – one entry per resource list page
//   Create            – jump-to /forms/* shortcut for the common writes
//
// Mounted once inside <RefineKbarProvider> at the top of <App/>. The visible
// "Search…  ⌘K" bar at the top of the sidebar (see AppSidebar.tsx) calls
// useKBar().query.toggle() to open the palette.
//
// Note: <RefineKbar/> (the package's convenience component) bundles BOTH the
// auto-registered CRUD actions we deliberately don't want AND the only
// visible search-dialog UI (portal/positioner/animator/search/results). We
// dropped <RefineKbar/> for the former reason but that also silently dropped
// the latter — this component composes the same primitives kbar itself uses
// (all publicly exported from "@refinedev/kbar"), minus the auto-registration,
// so Cmd-K actually opens a dialog again (#482).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  useRegisterActions,
  useMatches,
  KBarPortal,
  KBarPositioner,
  KBarAnimator,
  KBarSearch,
  KBarResults,
} from "@refinedev/kbar";
import {
  LayoutDashboard,
  ListTodo,
  Users2,
  Receipt,
  Users,
  ShieldCheck,
  Package,
  Truck,
  PackageCheck,
  ShoppingCart,
  Beaker,
  Calculator,
  Layers,
  Calendar,
  Building2,
  CalendarRange,
  Upload,
  Download,
  Library,
  Camera,
  Tag,
  BarChart3,
  Boxes,
  Play,
  FileSignature,
  Trophy,
  Scale,
  Workflow,
  UserCog,
  UserPlus,
  History,
  BookOpen,
  Search as SearchIcon,
  ScrollText,
  ClipboardList,
  MonitorPlay,
  Sparkles,
  FileText,
  Bell,
  FileSpreadsheet,
  Settings,
  Mail,
  Ruler,
} from "lucide-react";

import { mcp } from "@/lib/mcp";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { fetchManifest, type DocManifestEntry } from "@/lib/docs";
import { cn } from "@/lib/cn";

// The palette's results list — grouped by section, keyboard-navigable via
// kbar's own KBarResults. Kept intentionally simple (flat actions only, no
// nested/"root" actions), unlike RefineKbar's ResultItem which also renders
// an ancestor breadcrumb.
function CommandPaletteResults() {
  const { results } = useMatches();

  return (
    <KBarResults
      items={results}
      onRender={({ item, active }) =>
        typeof item === "string" ? (
          <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {item}
          </div>
        ) : (
          <div
            className={cn(
              "flex cursor-pointer items-center justify-between gap-2 border-l-2 px-4 py-2 text-sm",
              active ? "border-primary bg-accent" : "border-transparent",
            )}
          >
            <div className="flex items-center gap-2">
              {item.icon}
              <span>{item.name}</span>
            </div>
            {item.shortcut?.length ? (
              <div className="flex gap-1" aria-hidden="true">
                {item.shortcut.map((sc) => (
                  <kbd
                    key={sc}
                    className="rounded bg-muted px-1.5 py-0.5 text-xs"
                  >
                    {sc}
                  </kbd>
                ))}
              </div>
            ) : null}
          </div>
        )
      }
    />
  );
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();

  // Quick navigation – top of palette.
  useRegisterActions(
    [
      {
        id: "goto-dashboard",
        name: "Go to dashboard",
        keywords: "home start overview",
        section: "Navigation",
        perform: () => navigate("/dashboard"),
        icon: <LayoutDashboard className="h-4 w-4" />,
      },
      {
        id: "goto-production-dashboard",
        name: "Production dashboard",
        keywords: "production t-minus orders overdue critical traffic light shipping milestones jackie marchant tminus",
        section: "Navigation",
        perform: () => navigate("/dashboard/production"),
        icon: <BarChart3 className="h-4 w-4" />,
      },
      {
        id: "goto-management-dashboard",
        name: "Management overview",
        keywords: "management overview dashboard jon marchant hamza supplier scorecard qa production summary",
        section: "Navigation",
        perform: () => navigate("/dashboard/management"),
        icon: <BarChart3 className="h-4 w-4" />,
      },
      {
        id: "goto-reports",
        name: "Reports",
        keywords: "reports export meeting pack buyer master action items xlsx generate",
        section: "Navigation",
        perform: () => navigate("/reports"),
        icon: <Download className="h-4 w-4" />,
      },
      {
        id: "open-action-inbox",
        name: "Open Action inbox",
        keywords: "actions todo inbox triage",
        section: "Navigation",
        perform: () => navigate("/actions"),
        icon: <ListTodo className="h-4 w-4" />,
      },
      {
        // #471 — the email inbox. Two entries: one to browse, one that drops
        // straight into the search box, which is how staff actually arrive
        // ("what did we quote X?").
        id: "open-email-inbox",
        name: "Open Email inbox",
        keywords: "email emails inbox mail imap messages thread correspondence",
        section: "Navigation",
        perform: () => navigate("/emails"),
        icon: <Mail className="h-4 w-4" />,
      },
      {
        id: "search-emails",
        name: "Search emails",
        keywords:
          "email emails search find message subject sender body thread quote quoted correspondence mail",
        section: "Navigation",
        perform: () => navigate("/emails?focus=search"),
        icon: <SearchIcon className="h-4 w-4" />,
      },
    ],
    [navigate],
  );

  // One entry per resource list page. No auto-registered duplicates because
  // <RefineKbar /> is no longer mounted (see App.tsx).
  useRegisterActions(
    [
      {
        id: "browse-orders",
        name: "Orders",
        keywords: "orders production po list",
        section: "Browse",
        perform: () => navigate("/orders"),
        icon: <Package className="h-4 w-4" />,
      },
      {
        id: "browse-customers",
        name: "Customers",
        keywords: "customers retailers brands",
        section: "Browse",
        perform: () => navigate("/customers"),
        icon: <Users className="h-4 w-4" />,
      },
      {
        // W7.4: Critical Path is now universal. Cmd-K jumps to the admin
        // editor (catalog + templates). Per-customer cross-order grids live
        // at /customers/{id}/critical-path and aren't pre-registered in
        // Cmd-K — the customer profile page exposes a button.
        id: "order-workflow",
        name: "Critical Path workflow",
        keywords: "critical path workflow approval milestones template catalog order type truworths first fit lab dips trim sheets pps bps justine",
        section: "Browse",
        perform: () => navigate("/admin/order-workflow"),
        icon: <Workflow className="h-4 w-4" />,
      },
      {
        id: "browse-suppliers",
        name: "Suppliers",
        keywords: "suppliers factories vendors",
        section: "Browse",
        perform: () => navigate("/suppliers"),
        icon: <Building2 className="h-4 w-4" />,
      },
      {
        id: "browse-supplier-scorecard",
        name: "Supplier scorecard",
        keywords: "supplier scorecard performance ranking tier on time qa pass score factory rating",
        section: "Browse",
        perform: () => navigate("/suppliers/scorecard"),
        icon: <Trophy className="h-4 w-4" />,
      },
      {
        id: "browse-samples",
        name: "Samples",
        keywords: "samples pp pps development fit",
        section: "Browse",
        perform: () => navigate("/samples"),
        icon: <Beaker className="h-4 w-4" />,
      },
      {
        id: "browse-qa",
        name: "QA inspections",
        keywords: "qa inspections quality qima",
        section: "Browse",
        perform: () => navigate("/qa"),
        icon: <ShieldCheck className="h-4 w-4" />,
      },
      {
        id: "browse-qa-pending",
        name: "QA pending approvals",
        keywords: "qa pending approval queue mac qima review approve reject",
        section: "Browse",
        perform: () => navigate("/qa/pending"),
        icon: <ShieldCheck className="h-4 w-4" />,
      },
      {
        id: "browse-qa-dashboard",
        name: "QA dashboard",
        keywords: "qa dashboard scorecard pass fail rate supplier",
        section: "Browse",
        perform: () => navigate("/qa/dashboard"),
        icon: <BarChart3 className="h-4 w-4" />,
      },
      {
        id: "browse-shipments",
        name: "Shipments",
        keywords: "shipments containers freight",
        section: "Browse",
        perform: () => navigate("/shipments"),
        icon: <Truck className="h-4 w-4" />,
      },
      {
        id: "browse-pending-deliveries",
        name: "Pending deliveries",
        keywords: "pending deliveries warehouse pod confirm receive accept",
        section: "Browse",
        perform: () => navigate("/shipments/pending-delivery"),
        icon: <PackageCheck className="h-4 w-4" />,
      },
      {
        id: "browse-developments",
        name: "Product developments",
        keywords: "developments product dev",
        section: "Browse",
        perform: () => navigate("/developments"),
        icon: <Layers className="h-4 w-4" />,
      },
      {
        id: "browse-quotes",
        name: "Quotes",
        keywords: "quotes pricing fob",
        section: "Browse",
        perform: () => navigate("/quotes"),
        icon: <Receipt className="h-4 w-4" />,
      },
      {
        id: "browse-costings",
        name: "Style costings",
        keywords: "costings landed margin",
        section: "Browse",
        perform: () => navigate("/costings"),
        icon: <Calculator className="h-4 w-4" />,
      },
      {
        id: "browse-stock-offers",
        name: "Stock offers",
        keywords: "stock offers inventory",
        section: "Browse",
        perform: () => navigate("/stock-offers"),
        icon: <ShoppingCart className="h-4 w-4" />,
      },
      {
        id: "browse-meetings",
        name: "Meetings",
        keywords: "meetings visits calls",
        section: "Browse",
        perform: () => navigate("/meetings"),
        icon: <Calendar className="h-4 w-4" />,
      },
      {
        id: "search-loans",
        name: "Search loans",
        keywords: "loans library samples borrow lending borrower return",
        section: "Browse",
        perform: () => navigate("/library/loans"),
        icon: <Package className="h-4 w-4" />,
      },
    ],
    [navigate],
  );

  // Common write shortcuts — jump to the relevant /forms/* page.
  useRegisterActions(
    [
      {
        id: "form-log-meeting",
        name: "Log a meeting",
        keywords: "new create meeting visit call",
        section: "Create",
        perform: () => navigate("/forms/log-meeting"),
        icon: <Users2 className="h-4 w-4" />,
      },
      {
        id: "form-new-quote",
        name: "Add a quote",
        keywords: "new create quote supplier price",
        section: "Create",
        perform: () => navigate("/forms/new-quote"),
        icon: <Receipt className="h-4 w-4" />,
      },
      {
        id: "form-new-customer",
        name: "Add a customer",
        keywords: "new create customer retail brand",
        section: "Create",
        perform: () => navigate("/forms/new-customer"),
        icon: <Users className="h-4 w-4" />,
      },
      {
        id: "form-qa-result",
        name: "Submit QA inspection",
        keywords: "new create qa inspection quality result aql qima approval submit pdf upload",
        section: "Create",
        perform: () => navigate("/forms/qa-result"),
        icon: <ShieldCheck className="h-4 w-4" />,
      },
      {
        // #1065: "Generate PO" was a form that rendered a throwaway document;
        // the destination is now the Supplier POs list, where a stored PO is
        // previewed, sent and re-opened.
        id: "form-generate-po",
        name: "Supplier POs",
        keywords: "generate purchase order po ppo pdf xlsx supplier email send chrystal",
        section: "Create",
        perform: () => navigate("/supplier-pos"),
        icon: <FileSignature className="h-4 w-4" />,
      },
      {
        id: "create-loan",
        name: "Create new loan",
        keywords: "new create loan library sample borrow lend out",
        section: "Create",
        perform: () => navigate("/library/loans/new"),
        icon: <Package className="h-4 w-4" />,
      },
      {
        id: "library-import-samples",
        name: "Import samples",
        keywords: "import upload csv xlsx excel range library bulk supplier sheet",
        section: "Create",
        perform: () => navigate("/library/import"),
        icon: <Upload className="h-4 w-4" />,
      },
      {
        id: "library-export-samples",
        name: "Export samples",
        keywords: "export download pptx pdf xlsx powerpoint excel range library meeting branded",
        section: "Create",
        perform: () => navigate("/library/export"),
        icon: <Download className="h-4 w-4" />,
      },
    ],
    [navigate],
  );

  // Sample Library — Browse / Create / Action / Admin shortcuts in their own
  // group so the Cmd-K palette has a single, scannable surface for everything
  // sample-library related. (The Browse/Create blocks above already include
  // a few library entries that grew up with loans + import + export; those
  // stay there for backwards-compat. kbar dedupes by id, so the entries
  // below intentionally use distinct `lib-*` ids.)
  useRegisterActions(
    [
      // Browse
      {
        id: "lib-browse-library",
        name: "Browse library",
        keywords: "library samples browse list",
        section: "Sample Library",
        perform: () => navigate("/library"),
        icon: <Library className="h-4 w-4" />,
      },
      {
        id: "lib-browse-loans",
        name: "Browse loans",
        keywords: "library loans out borrowed",
        section: "Sample Library",
        perform: () => navigate("/library/loans"),
        icon: <Package className="h-4 w-4" />,
      },
      {
        id: "lib-browse-meetings",
        name: "Browse meetings",
        keywords: "library meetings visits calls",
        section: "Sample Library",
        perform: () => navigate("/meetings"),
        icon: <Users2 className="h-4 w-4" />,
      },
      {
        id: "lib-dashboard",
        name: "Library dashboard",
        keywords: "library dashboard overview kpi",
        section: "Sample Library",
        perform: () => navigate("/library/dashboard"),
        icon: <BarChart3 className="h-4 w-4" />,
      },
      {
        id: "lib-browse-briefs",
        name: "Briefs",
        keywords: "library briefs board pipeline meeting outcome kanban",
        section: "Sample Library",
        perform: () => navigate("/briefs"),
        icon: <ClipboardList className="h-4 w-4" />,
      },
      {
        id: "lib-browse-presentations",
        name: "Presentations",
        keywords: "library presentations deck buyer show range",
        section: "Sample Library",
        perform: () => navigate("/library/presentations"),
        icon: <MonitorPlay className="h-4 w-4" />,
      },
      {
        id: "lib-presentations-history",
        name: "Presentation history",
        keywords: "library presentations history past decks archive",
        section: "Sample Library",
        perform: () => navigate("/library/presentations/history"),
        icon: <History className="h-4 w-4" />,
      },
      {
        id: "lib-ai-images",
        name: "AI images",
        keywords: "library ai images generate garment render",
        section: "Sample Library",
        perform: () => navigate("/library/ai-images"),
        icon: <Sparkles className="h-4 w-4" />,
      },
      // Create
      {
        id: "lib-new-sample",
        name: "New sample",
        keywords: "library new create sample item add",
        section: "Sample Library",
        perform: () => navigate("/library/new"),
        icon: <Library className="h-4 w-4" />,
      },
      {
        id: "lib-new-loan",
        name: "New loan",
        keywords: "library new create loan borrow checkout",
        section: "Sample Library",
        perform: () => navigate("/library/loans/new"),
        icon: <Package className="h-4 w-4" />,
      },
      // Action
      {
        id: "lib-scan",
        name: "Scan sample",
        keywords: "library scan qr barcode camera",
        section: "Sample Library",
        perform: () => navigate("/library/scan"),
        icon: <Camera className="h-4 w-4" />,
      },
      {
        id: "lib-print-labels",
        name: "Print labels",
        keywords: "library labels print qr barcode",
        section: "Sample Library",
        perform: () => navigate("/library/labels"),
        icon: <Tag className="h-4 w-4" />,
      },
      {
        id: "lib-import",
        name: "Import samples",
        keywords: "library import upload csv excel",
        section: "Sample Library",
        perform: () => navigate("/library/import"),
        icon: <Upload className="h-4 w-4" />,
      },
      {
        id: "lib-export",
        name: "Export samples",
        keywords: "library export download csv pdf pptx",
        section: "Sample Library",
        perform: () => navigate("/library/export"),
        icon: <Download className="h-4 w-4" />,
      },
      // Admin
      {
        id: "lib-tags",
        name: "Manage tags",
        keywords: "library tags admin colour color label chip stock offer",
        section: "Sample Library",
        perform: () => navigate("/library/tags"),
        icon: <Tag className="h-4 w-4" />,
      },
      {
        id: "lib-tags-new",
        name: "Create tag",
        keywords: "library tag new create colour color admin",
        section: "Sample Library",
        perform: () => navigate("/library/tags?action=new"),
        icon: <Tag className="h-4 w-4" />,
      },
      {
        id: "lib-locations",
        name: "Manage storage locations",
        keywords: "library locations storage admin shelf rack",
        section: "Sample Library",
        perform: () => navigate("/library/locations"),
        icon: <Boxes className="h-4 w-4" />,
      },
      {
        id: "lib-admin-hub",
        name: "Library admin",
        keywords: "library admin hub tools tags locations import export",
        section: "Sample Library",
        perform: () => navigate("/library/admin"),
        icon: <Settings className="h-4 w-4" />,
      },
    ],
    [navigate],
  );

  // Seasons & SKUs — direct jumps to the SKU manager for each active season.
  useRegisterActions(
    [
      {
        id: "seasons-overview",
        name: "Browse seasons",
        keywords: "seasons overview list aw26 ss26 aw27",
        section: "Seasons & SKUs",
        perform: () => navigate("/seasons"),
        icon: <CalendarRange className="h-4 w-4" />,
      },
      {
        id: "manage-ss26-skus",
        name: "Manage SS26 SKUs",
        keywords: "ss26 spring summer 2026 skus styles range booking",
        section: "Seasons & SKUs",
        perform: () => navigate("/seasons/SS26/skus"),
        icon: <CalendarRange className="h-4 w-4" />,
      },
      {
        id: "manage-aw26-skus",
        name: "Manage AW26 SKUs",
        keywords: "aw26 autumn winter 2026 skus styles range",
        section: "Seasons & SKUs",
        perform: () => navigate("/seasons/AW26/skus"),
        icon: <CalendarRange className="h-4 w-4" />,
      },
      {
        id: "manage-aw27-skus",
        name: "Manage AW27 SKUs",
        keywords: "aw27 autumn winter 2027 skus styles range development",
        section: "Seasons & SKUs",
        perform: () => navigate("/seasons/AW27/skus"),
        icon: <CalendarRange className="h-4 w-4" />,
      },
    ],
    [navigate],
  );

  // Recent meetings — Cmd-K shortcut to enter Meeting Mode. Pulls the most
  // recent 5 meetings and registers one entry per meeting that routes to
  // /meetings/:id/mode. Fetched once on mount; the palette deduplicates by id.
  const [recentMeetings, setRecentMeetings] = useState<
    Array<{ id: string; title: string; subtitle: string }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    mcp.listMeetings({ limit: 5 })
      .then((res) => {
        if (cancelled) return;
        const items = (res.meetings ?? []).map((m) => {
          const id = String(m.id ?? "");
          const title = String(m.title ?? "(untitled meeting)");
          const date = m.meeting_date
            ? new Date(String(m.meeting_date)).toLocaleDateString("en-ZA", {
              month: "short",
              day: "numeric",
            })
            : "";
          return { id, title, subtitle: date };
        }).filter((m) => m.id);
        setRecentMeetings(items);
      })
      .catch(() => { if (!cancelled) setRecentMeetings([]); });
    return () => { cancelled = true; };
  }, []);

  useRegisterActions(
    [
      {
        id: "meeting-mode-recent",
        name: "Enter Meeting Mode",
        keywords: "meeting mode present samples buyers presentation live",
        section: "Meeting Mode",
        perform: () => navigate("/meetings"),
        icon: <Play className="h-4 w-4" />,
      },
      ...recentMeetings.map((m) => ({
        id: `meeting-mode-${m.id}`,
        name: m.title,
        keywords: `meeting mode present ${m.title.toLowerCase()} ${m.subtitle}`,
        section: "Meeting Mode",
        perform: () => navigate(`/meetings/${m.id}/mode`),
        icon: <Play className="h-4 w-4" />,
        subtitle: m.subtitle,
      })),
    ],
    [navigate, recentMeetings],
  );

  // Settings — admin and finance surfaces. Visible to everyone in Cmd-K; the
  // page enforces admin role and RLS gates the writes.
  useRegisterActions(
    [
      {
        id: "settings-factor-rules",
        name: "Factor rules",
        keywords:
          "finance pricing factor rules governor multiplier zar usd settings admin",
        section: "Settings",
        perform: () => navigate("/finance/factor-rules"),
        icon: <Scale className="h-4 w-4" />,
      },
      {
        id: "account-mfa",
        name: "Two-factor authentication",
        keywords:
          "mfa 2fa totp authenticator account security factor enrol enroll register google authy 1password aal2",
        section: "Settings",
        perform: () => navigate("/account/mfa"),
        icon: <ShieldCheck className="h-4 w-4" />,
      },
    ],
    [navigate],
  );

  // Admin — user management entries, gated to portal admins. The pages
  // themselves also enforce admin via useIsAdmin() and the verbs are RLS-
  // gated, so this is a UX nicety rather than the only line of defence.
  useRegisterActions(
    isAdmin
      ? [
        {
          id: "admin-manage-users",
          name: "Manage users",
          keywords:
            "admin users management portal accounts roles staff disable enable",
          section: "Admin",
          perform: () => navigate("/admin/users"),
          icon: <UserCog className="h-4 w-4" />,
        },
        {
          id: "admin-invite-user",
          name: "Invite user",
          keywords:
            "admin invite user new account email signup add staff member onboard",
          section: "Admin",
          perform: () => navigate("/admin/users/invite"),
          icon: <UserPlus className="h-4 w-4" />,
        },
        {
          id: "admin-user-audit-log",
          name: "User audit log",
          keywords:
            "admin user audit log history changes role grants revocations who when",
          section: "Admin",
          perform: () => navigate("/admin/users"),
          icon: <History className="h-4 w-4" />,
        },
        {
          id: "admin-settings",
          name: "System settings",
          keywords: "admin settings config system general",
          section: "Admin",
          perform: () => navigate("/admin/settings"),
          icon: <Settings className="h-4 w-4" />,
        },
        {
          id: "admin-seasons",
          name: "Manage seasons",
          keywords: "admin seasons aw26 ss26 aw27 skus range",
          section: "Admin",
          perform: () => navigate("/admin/seasons"),
          icon: <CalendarRange className="h-4 w-4" />,
        },
        {
          id: "admin-tags",
          name: "Manage tags",
          keywords: "admin tags taxonomy colour color label chip",
          section: "Admin",
          perform: () => navigate("/admin/tags"),
          icon: <Tag className="h-4 w-4" />,
        },
        {
          id: "admin-doc-types",
          name: "Manage document types",
          keywords: "admin doc types documents files upload categories",
          section: "Admin",
          perform: () => navigate("/admin/doc-types"),
          icon: <FileText className="h-4 w-4" />,
        },
        {
          id: "admin-locations",
          name: "Manage storage locations",
          keywords: "admin locations storage shelf rack",
          section: "Admin",
          perform: () => navigate("/admin/locations"),
          icon: <Boxes className="h-4 w-4" />,
        },
        {
          id: "admin-notifications",
          name: "Manage notifications",
          keywords: "admin notifications alerts email digest",
          section: "Admin",
          perform: () => navigate("/admin/notifications"),
          icon: <Bell className="h-4 w-4" />,
        },
        {
          id: "admin-export-templates",
          name: "Manage export templates",
          keywords: "admin export templates pptx pdf xlsx",
          section: "Admin",
          perform: () => navigate("/admin/export-templates"),
          icon: <FileSpreadsheet className="h-4 w-4" />,
        },
        {
          id: "admin-ai-usage",
          name: "AI usage",
          keywords: "admin ai usage cost tokens spend billing",
          section: "Admin",
          perform: () => navigate("/admin/ai-usage"),
          icon: <Sparkles className="h-4 w-4" />,
        },
        {
          id: "admin-sizes",
          name: "Manage sizes",
          keywords:
            "admin sizes size sets curves matrix prepacks pre-packs packs singles scale xxs xs s m l xl matrix code",
          section: "Admin",
          perform: () => navigate("/admin/sizes"),
          icon: <Ruler className="h-4 w-4" />,
        },
        {
          id: "admin-brands",
          name: "Manage brands",
          keywords: "admin brands brand create rename reparent parent archive company crm",
          section: "Admin",
          perform: () => navigate("/admin/brands"),
          icon: <Building2 className="h-4 w-4" />,
        },
      ]
      : [],
    [navigate, isAdmin],
  );

  // Docs — fixed entry points to the in-app documentation surface. Visible
  // to every authenticated user. Per-workflow shortcuts are registered
  // separately below once the manifest loads.
  useRegisterActions(
    [
      {
        id: "docs-open-user-manual",
        name: "Open user manual",
        keywords:
          "docs help manual user guide handbook portal-user-guide reference how to instructions",
        section: "Help & Docs",
        perform: () => navigate("/docs/user-guide"),
        icon: <BookOpen className="h-4 w-4" />,
      },
      {
        id: "docs-open-quickstart",
        name: "Quickstart guide",
        keywords:
          "docs help quickstart quick start onboarding 30 minute first time new staff",
        section: "Help & Docs",
        perform: () => navigate("/docs/quickstart"),
        icon: <BookOpen className="h-4 w-4" />,
      },
      {
        id: "docs-browse-workflows",
        name: "Browse workflows",
        keywords:
          "docs help workflows business process operations sops 17 workflow",
        section: "Help & Docs",
        perform: () => navigate("/docs/workflows"),
        icon: <Workflow className="h-4 w-4" />,
      },
      {
        id: "docs-search",
        name: "Search docs",
        keywords:
          "docs help search find lookup query manual workflow glossary",
        section: "Help & Docs",
        perform: () => navigate("/docs?focus=search"),
        icon: <SearchIcon className="h-4 w-4" />,
      },
      {
        id: "docs-glossary",
        name: "Glossary",
        keywords:
          "docs help glossary terms definitions abbreviations acronyms",
        section: "Help & Docs",
        perform: () => navigate("/docs/glossary"),
        icon: <ScrollText className="h-4 w-4" />,
      },
      {
        id: "docs-popia",
        name: "POPIA compliance",
        keywords:
          "docs help popia compliance privacy data protection policy gdpr",
        section: "Help & Docs",
        perform: () => navigate("/docs/popia"),
        icon: <ShieldCheck className="h-4 w-4" />,
      },
    ],
    [navigate],
  );

  // Per-workflow shortcuts — one entry per workflow doc, fetched from the
  // manifest at mount time. Lets staff jump straight to "Workflow: Quote to
  // PO" without browsing the index.
  const [workflowDocs, setWorkflowDocs] = useState<DocManifestEntry[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchManifest()
      .then((m) => {
        if (cancelled) return;
        setWorkflowDocs(
          m.docs.filter((d) => d.category === "workflows" && d.slug !== "workflows-index"),
        );
      })
      .catch(() => {
        if (!cancelled) setWorkflowDocs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useRegisterActions(
    workflowDocs.map((doc) => ({
      id: `docs-${doc.slug}`,
      name: `Workflow: ${doc.title}`,
      keywords: `docs help workflow ${doc.title.toLowerCase()} ${doc.slug}`,
      section: "Help & Docs",
      perform: () => navigate(doc.route),
      icon: <Workflow className="h-4 w-4" />,
    })),
    [navigate, workflowDocs],
  );

  return (
    <KBarPortal>
      {/* KBarPositioner's TS props only cover className/style, not arbitrary
          aria attrs, so the dialog role/label live on this plain wrapper —
          it has no layout of its own, KBarPositioner is still what applies
          the fixed full-viewport overlay. */}
      <div role="dialog" aria-modal="true" aria-label="Command palette">
        <KBarPositioner className="z-50 bg-black/40 backdrop-blur-sm">
          <KBarAnimator className="w-full max-w-xl overflow-hidden rounded-lg border bg-background shadow-2xl">
            <KBarSearch className="w-full border-b bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground" />
            <div className="max-h-[60vh] overflow-y-auto py-2">
              <CommandPaletteResults />
            </div>
          </KBarAnimator>
        </KBarPositioner>
      </div>
    </KBarPortal>
  );
}
